import React, { useState, useEffect, useRef } from 'react';
import { ApolloClient } from '@apollo/client';
import { JobProperties, TransformedJobProperties } from './JobProperties';
import getTemplateProperties from '../../../lib/getJobTemplateProperties';
import importTemplate, {
  TemplatesProperties,
} from '../../../lib/importJobTemplate';
import transformJob from '../../../lib/transformJob';
import Error from '../../Error';
import { useSiteContext } from '../../SiteContext';
import getJobEventTracker from '../../../lib/getJobEventTracker';

export interface JobTemplateProperties {
  job: JobProperties;
  locale: string;
  apolloClient: ApolloClient<object>;
}

const JobTemplate = ({
  job,
  locale,
  apolloClient,
}: JobTemplateProperties) => {
  const transformedJob = transformJob(job);
  const templateProperties: {
    job: TransformedJobProperties;
    locale: string;
    applyUrl: string | null;
    profiles: Record<string, {
      id: string;
      page: {
        slug: string;
        title: string;
      };
      company: {
        fullName: string;
      };
    }>;
  } = getTemplateProperties(
    transformedJob,
    locale,
  );
  const [templates, setTemplates] = useState<TemplatesProperties>({});
  const [error, setError] = useState<string | null>(null);
  const [
    applyButtonTracker,
    setApplyButtonTracker,
  ] = useState<((event: Event) => void) | null>(null);
  const { origin } = useSiteContext();
  const {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    _id,
    applyForm,
    city,
    company: { name: companyName },
    id,
    template: { sourceName, revision },
    title,
  } = job;
  const reference = useRef<HTMLDivElement>(null);

  useEffect(() => {
    async function fetchTemplates() {
      try {
        const fetchedTemplates = await importTemplate(
          sourceName,
          revision,
          origin,
        );
        setTemplates(fetchedTemplates);
        setError(null);
      } catch (importError) {
        setTemplates({});
        setError(String(importError));
      }
    }

    fetchTemplates();
  }, [origin, revision, sourceName]);

  useEffect(() => {
    const jobForEventTracker = {
      id,
      title,
      company: { name: companyName },
      city,
      _id,
    };

    setApplyButtonTracker(
      () => (applyForm
        ? getJobEventTracker(
          apolloClient,
          'Meteor Job universal apply button',
          jobForEventTracker,
        ) : getJobEventTracker(
          apolloClient,
          'Meteor Job apply button',
          jobForEventTracker,
        )),
    );
  }, [apolloClient, applyForm, id, title, companyName, city, _id]);

  const { CompanyDescription, Description } = templates;

  useEffect(
    () => {
      const { current } = reference;

      if (applyButtonTracker) {
        current?.querySelectorAll(
          '.fu-btn-job-apply, .fu-universal-apply-button',
        ).forEach((applyButton) => {
          applyButton.addEventListener(
            'click', applyButtonTracker,
          );
        });

        return () => {
          current?.querySelectorAll(
            '.fu-btn-job-apply, .fu-universal-apply-button',
          ).forEach((applyButton) => {
            applyButton.removeEventListener(
              'click', applyButtonTracker,
            );
          });
        };
      }

      return () => {};
    },
    [applyButtonTracker, CompanyDescription, Description],
  );

  return (
    <div ref={reference}>
      { error && <Error error="Error loading job details" /> }
      { CompanyDescription && <CompanyDescription {...templateProperties} /> }
      { Description && <Description {...templateProperties} /> }
    </div>
  );
};

export default JobTemplate;
